// FilterPanel.js (updated with detection class filter)
import React, { useState, useEffect } from 'react';
import Icon from '../../../components/appIcon';
import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';

const FilterPanel = ({
  filters,
  cameras,
  onFiltersChange,
  onReset,
  isCollapsed = false,
  onToggle,
  handleTimeFilterChange,
  timeFilter
}) => {
  const [localFilters, setLocalFilters] = useState(filters);

  // Validate that dateFrom is not after dateTo
  useEffect(() => {
    if (localFilters.dateFrom && localFilters.dateTo) {
      const fromDate = new Date(localFilters.dateFrom);
      const toDate = new Date(localFilters.dateTo);

      if (fromDate > toDate) {
        // Reset dateTo if it's before dateFrom
        const updatedFilters = { ...localFilters, dateTo: '' };
        setLocalFilters(updatedFilters);
        onFiltersChange(updatedFilters);
      }
    }
  }, [localFilters.dateFrom, localFilters.dateTo, onFiltersChange]);

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'new', label: 'New' },
    { value: 'dismissed', label: 'Dismissed' }
  ];

  // Use cameras from API or default to static list
  const cameraLocations = cameras.length > 0
    ? ['All Locations', ...cameras.map(camera => camera.name)]
    : [
      'All Locations',
      'Main Entrance',
      'Reception Area',
      'Parking Lot',
      'Side Entrance',
      'Cafeteria',
      'Office Floor 1',
      'Office Floor 2',
      'Emergency Exit'
    ];

  const detectionClassOptions = [
    { value: 'all', label: 'All Classes' },
    { value: 'person', label: 'Person' },
    { value: 'mask', label: 'Mask' },
    { value: 'helmet', label: 'Helmet' }
  ];

  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...localFilters, [key]: value };
    setLocalFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      dateFrom: '',
      dateTo: '',
      status: 'all',
      location: 'All Locations',
      detectionClass: 'all', // Add detection class to reset
      confidenceMin: 0,
      confidenceMax: 100,
      search: ''
    };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
    onReset();
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters?.dateFrom) count++;
    if (localFilters?.dateTo) count++;
    if (localFilters?.status !== 'all') count++;
    if (localFilters?.location !== 'All Locations') count++;
    if (localFilters?.detectionClass !== 'all') count++; // Add detection class to count
    if (localFilters?.confidenceMin > 0) count++;
    if (localFilters?.confidenceMax < 100) count++;
    if (localFilters?.search) count++;
    return count;
  };

  const hasActiveFilters = getActiveFilterCount() > 0;

  // Collapsed State
  if (isCollapsed) {
    return (
      <button
        onClick={onToggle}
        className="group relative flex items-center gap-2 px-4 py-2.5 text-sm bg-white rounded-lg hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden transform hover:-translate-y-0.5 mb-5"
      >
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        
        {/* Animated circle decoration */}
        <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-20 group-hover:scale-150 transition-transform duration-700"></div>
        
        <div className="relative z-10 flex items-center justify-between ">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 text-blue-600 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
              <Icon name="Filter" size={16} />
            </div>
            <span className="font-medium text-gray-700 group-hover:text-blue-600 transition-colors duration-300">
              Show Filters
            </span>
            {hasActiveFilters && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                {getActiveFilterCount()}
              </span>
            )}
          </div>
          <Icon name="ChevronDown" size={16} className="text-gray-500" />
        </div>
      </button>
    );
  }

  // Expanded State
  return (
    <div className="relative bg-white rounded-xl p-5 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden group mb-5">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-purple-50/30 to-pink-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
      
      {/* Decorative circles */}
      <div className="absolute -top-12 -right-12 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-10 group-hover:scale-150 transition-transform duration-1000"></div>
      <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full opacity-10 group-hover:scale-150 transition-transform duration-1000"></div>

      <div className="relative z-10 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 text-blue-600">
              <Icon name="Filter" size={18} />
            </div>
            <h3 className="font-semibold text-gray-800">Filters</h3>
            {hasActiveFilters && (
              <span className="px-2.5 py-1 bg-gradient-to-br from-blue-100 to-purple-100 text-blue-700 text-xs rounded-full font-medium">
                {getActiveFilterCount()} active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button
                onClick={handleReset}
                className="group/btn h-9 px-3 text-xs text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1.5 transition-all duration-300 transform hover:scale-105"
              >
                <Icon name="RotateCcw" size={14} className="group-hover/btn:rotate-180 transition-transform duration-300" />
                Reset
              </button>
            )}
            <button
              onClick={onToggle}
              className="h-9 px-2 text-gray-500 hover:text-pink-600 hover:bg-pink-50 rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              <Icon name="ChevronUp" size={16} className="hover:translate-y-0.5 transition-transform duration-300" />
            </button>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Date Range */}
          <div className="space-y-3">
            <h4 className="font-semibold text-xs text-gray-600 uppercase tracking-wider">
              Date Range
            </h4>
            <div className="space-y-2">
              <div className="transform transition-all duration-300 hover:scale-[1.02]">
                <Input
                  type="date"
                  label="From Date"
                  value={localFilters?.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
                  className="h-9 text-sm border-gray-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all duration-300"
                />
              </div>
              <div className="transform transition-all duration-300 hover:scale-[1.02]">
                <Input
                  type="date"
                  label="To Date"
                  value={localFilters?.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
                  className="h-9 text-sm border-gray-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all duration-300"
                />
              </div>
              {localFilters.dateFrom && localFilters.dateTo && (
                <div className="text-xs text-gray-500 bg-purple-50 px-2 py-1.5 rounded-md">
                  {localFilters.dateFrom} to {localFilters.dateTo}
                </div>
              )}
            </div>
          </div>

          {/* Location */}
          <div className="space-y-3">
            <h4 className="font-semibold text-xs text-gray-600 uppercase tracking-wider">
              Location
            </h4>
            <div className="transform transition-all duration-300 hover:scale-[1.02]">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Camera Location
              </label>
              <select
                value={localFilters?.location}
                onChange={(e) => handleFilterChange('location', e?.target?.value)}
                className="w-full h-9 px-3 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all duration-300"
              >
                {cameraLocations?.map(location => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Search */}
          <div className="space-y-3">
            <h4 className="font-semibold text-xs text-gray-600 uppercase tracking-wider">
              Search
            </h4>
            <div className="transform transition-all duration-300 hover:scale-[1.02]">
              <Input
                type="text"
                label="Search"
                placeholder="Search by ID, location..."
                value={localFilters?.search}
                onChange={(e) => handleFilterChange('search', e?.target?.value)}
                className="h-9 text-sm border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-100 transition-all duration-300"
              />
            </div>
          </div>
        </div>

        {/* Time Period Filters */}
        <div className="bg-gradient-to-br from-blue-50/50 to-purple-50/50 p-4 rounded-lg border border-blue-100">
          <label className="block text-xs font-semibold text-gray-700 mb-2.5">Time Period</label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleTimeFilterChange('today')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-300 transform hover:scale-105 ${
                timeFilter === 'today'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-blue-50 border border-gray-200'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => handleTimeFilterChange('week')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-300 transform hover:scale-105 ${
                timeFilter === 'week'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-purple-50 border border-gray-200'
              }`}
            >
              This Week
            </button>
            <button
              onClick={() => handleTimeFilterChange('month')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-300 transform hover:scale-105 ${
                timeFilter === 'month'
                  ? 'bg-pink-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-pink-50 border border-gray-200'
              }`}
            >
              This Month
            </button>
            <button
              onClick={() => handleTimeFilterChange('year')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-300 transform hover:scale-105 ${
                timeFilter === 'year'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-blue-50 border border-gray-200'
              }`}
            >
              This Year
            </button>
          </div>
        </div>

        {/* Active Filter Tags */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-1.5 pt-3 border-t border-gray-100">
            {localFilters?.dateFrom && (
              <FilterTag 
                label={`From: ${localFilters.dateFrom}`} 
                onRemove={() => handleFilterChange('dateFrom', '')} 
                color="purple" 
              />
            )}
            {localFilters?.dateTo && (
              <FilterTag 
                label={`To: ${localFilters.dateTo}`} 
                onRemove={() => handleFilterChange('dateTo', '')} 
                color="purple" 
              />
            )}
            {localFilters?.location !== 'All Locations' && (
              <FilterTag 
                label={`Location: ${localFilters.location}`} 
                onRemove={() => handleFilterChange('location', 'All Locations')} 
                color="blue" 
              />
            )}
            {localFilters?.search && (
              <FilterTag 
                label={`Search: ${localFilters.search}`} 
                onRemove={() => handleFilterChange('search', '')} 
                color="pink" 
              />
            )}
          </div>
        )}
      </div>

      {/* Animated pulse dots */}
      <div className="absolute top-4 right-4 flex space-x-1">
        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse"></div>
        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '75ms' }}></div>
        <div className="w-1.5 h-1.5 bg-pink-400 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
      </div>
    </div>
  );
};

const FilterTag = ({ label, onRemove, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200",
    purple: "bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200",
    pink: "bg-pink-50 hover:bg-pink-100 text-pink-700 border-pink-200"
  };

  return (
    <span 
      className={`group/tag inline-flex items-center gap-1.5 ${colorClasses[color]} border text-xs px-2.5 py-1.5 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-sm`}
    >
      <span className="max-w-[150px] truncate font-medium">{label}</span>
      <button 
        onClick={onRemove}
        className="text-current opacity-60 hover:opacity-100 transition-all duration-200 transform hover:rotate-90"
      >
        <Icon name="X" size={12} />
      </button>
    </span>
  );
};

export default FilterPanel;